import { destr } from 'destr'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import type {
  CrudInstance,
  CrudManagerModuleName,
  CrudManagerModuleSettings,
  CrudModel,
  CrudModule,
  CrudObject,
  ModelMetadata,
} from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { i18n } from 'src/CrudManager/Core/Utils/i18n'
import { getModelMetadata } from 'src/CrudManager/CrudModels/generated-crud'
import { Router } from 'src/router'
import { type Component } from 'vue'
import { externalEditorRouteFor } from './externalEditorRoutes'

/**
 * CRUD utilities composable with flexible context handling
 * @param options - Optional router and i18n instances for use outside Vue component context
 * @param options.router - Vue router instance (falls back to useRouter() if not provided)
 * @param options.i18n - Vue i18n instance (falls back to useI18n() if not provided)
 * @returns CRUD utility functions
 * @security Validates user permissions for CRUD operations
 * @performance Lazy loads full model configurations when needed
 */
export default function useCrudUtils() {
  // Try to use provided instances first, fall back to composables if in Vue context

  const crudManagerStore = useCrudManagerStore()
  const userStore = useUserStore()
  // Reactive title and subtitle

  // Load title and subtitle asynchronously
  const loadTitleAndSubtitle = async (instanceId: string) => {
    if (!instanceId) return

    try {
      const [title, subtitle] = await Promise.all([getInstanceTitle(instanceId), getInstanceSubtitle(instanceId)])

      return { title, subtitle }
    } catch (error) {
      console.warn('Failed to load title/subtitle:', error)
      return { title: '', subtitle: '' }
    }
  }

  const getModuleConfig = async (modelName: string, moduleName: CrudManagerModuleName): Promise<CrudManagerModuleSettings | undefined> => {
    try {
      const model = await crudManagerStore.getFullModel(modelName)
      if (!model) return

      const module = model.config?.[moduleName]

      return module
    } catch (error) {
      console.warn(`Could not load model config for ${modelName}:`, error)
      return undefined
    }
  }

  const getInstanceById = (id: string) => {
    return crudManagerStore.crudInstances.find((item: CrudInstance) => item.id === id)
  }

  const getModule = (moduleName: string) => {
    if (!crudManagerStore.availablesModules?.length) {
      console.error('crud manager is not yet initiated')
      return
    }
    if (!moduleName) {
      console.warn(`module name is ${moduleName}`)
      return false
    }
    // External recents point at standalone screens and have no CRUD module.
    if (moduleName === 'external') return false

    const module = crudManagerStore.availablesModules.find((m: CrudModule) => m.name.toLowerCase() === moduleName.toLowerCase())
    if (!module) {
      console.error(`module not found: ${moduleName}`)
      return false
    }

    return module
  }

  /**
   * Gets model metadata (lightweight) - use this for basic properties
   * @param modelName - Name of the model
   * @returns Model metadata or undefined if not found
   */
  const getModelMeta = (modelName: string): ModelMetadata | undefined => {
    if (!crudManagerStore.availablesModels?.length) {
      console.error('crud manager is not initialized')
      return
    }

    if (!modelName) {
      console.error('model is not found: ' + modelName)
      return
    }

    return getModelMetadata(modelName)
  }

  /**
   * Gets full model configuration (lazy loaded) - use only when needed
   * @param modelName - Name of the model
   * @returns Promise resolving to full model configuration
   */
  const getFullModel = async (modelName: string): Promise<CrudModel<CrudObject> | undefined> => {
    if (!crudManagerStore.availablesModels?.length) {
      console.error('crud manager is not initialized')
      return
    }

    if (!modelName) {
      console.error('model is not found: ' + modelName)
      return
    }

    try {
      return await crudManagerStore.getFullModel(modelName)
    } catch (error) {
      console.error(`Failed to load model: ${modelName}`, error)
      return
    }
  }

  const getModuleBySlug = (slug: string): CrudModule | undefined => {
    return crudManagerStore.availablesModules.find((m: CrudModule) => m.slugs.includes(slug))
  }

  const getModelBySlug = (slug: string) => {
    if (!slug) return null

    return crudManagerStore.availablesModels.find((model) => model.slugs && Array.isArray(model.slugs) && model.slugs.includes(slug))
  }

  const getChildModels = (parentId: string) => {
    return crudManagerStore.crudInstances.filter((instance: CrudInstance) => instance.parentId === parentId)
  }

  const canUserCrud = (modelName: string, permission: string) => {
    const meta = getModelMetadata(modelName)
    const permissionModel = meta?.permissionAlias || modelName
    return userStore.hasPermission(`cm-${permissionModel}_${permission}`)
  }

  const getModelIcon = async (modelName: string) => {
    const modelMeta = getModelMeta(modelName)
    if (modelMeta?.icon) {
      return modelMeta.icon
    }

    // Fallback to full model for backward compatibility
    try {
      const model = await getFullModel(modelName)
      return model?.icon
    } catch {
      return undefined
    }
  }

  const getModelConfig = async (modelName: string) => {
    try {
      const model = await getFullModel(modelName)
      return model?.config
    } catch {
      return undefined
    }
  }

  const getModelComponent = async (modelName: string, componentName: string) => {
    try {
      const config = await getModelConfig(modelName)
      if (!config) return false

      const parts = componentName.split('.')

      if (parts.length > 1 && config && config[parts[0]] && config[parts[0]].components && config[parts[0]].components[parts[1]]) {
        const component = config[parts[0]].components[parts[1]] as Component

        return component
      }

      return false
    } catch {
      return false
    }
  }

  const getModuleComponent = async (moduleName: string, componentName: string) => {
    const availableModules = crudManagerStore.availablesModules
    const module = availableModules.find((m) => m.name === moduleName)
    if (!module) return false
    const component = module[componentName]
    if (!component) return false
    return component
  }

  const openEditor = async (modelName: string, id: string, makeVisible = true, dialog = false) => {
    // A registered external route replaces the tab editor as the record's
    // front door; callers receive null instead of an instance.
    const externalRoute = externalEditorRouteFor(modelName)
    if (externalRoute && Router) {
      if (makeVisible) {
        await Router.push(externalRoute(id))
      }
      return null
    }

    // Create the instance without displaying it if makeVisible is false
    const instance = crudManagerStore.createInstance(modelName, 'editor', { modelId: id, useDialog: dialog }, null, makeVisible)

    if (makeVisible && !dialog && Router) {
      // Generate and use the proper URL for the editor
      const editorUrl = generateModuleUrl(modelName, 'editor', id)
      if (editorUrl) {
        Router.push(editorUrl)
      }
    }

    return instance
  }

  const openCreator = (modelName: string, data: Record<string, unknown> | null = null) => {
    if (!Router || !i18n) {
      console.warn('openCreator requires router and i18n instances')
      return
    }

    data = data || {}
    const modelSlug = i18n.t(`crudmodels.${modelName}.slug`)
    // convert data to url params
    const dataParams = new URLSearchParams(data as Record<string, string>).toString()

    Router.push(`/manage/${modelSlug}/create?${dataParams}`)
  }

  const redirectToLister = async (modelName: string) => {
    if (!Router || !i18n) {
      console.warn('redirectToLister requires router and i18n instances')
      return
    }

    const modelMeta = getModelMeta(modelName)
    if (!modelMeta) return

    // Don't redirect if the instance is a dialog
    const instance = crudManagerStore.crudInstances.find((i) => i.modelName === modelName && i.status.isDialog)
    if (instance) return

    const modelSlug = i18n.t(`crudmodels.${modelName}.slug`)
    Router.push(`/manage/${modelSlug}/list`)
  }

  const instanceExists = (modelName: string, moduleName: string, id: string | null = null) => {
    return crudManagerStore.crudInstances.find((item: CrudInstance) => {
      const matchesModel = item.modelName === modelName
      const matchesModule = item.moduleName === moduleName

      if (id) {
        // Check both data.id and data.item.id since the structure can vary
        const instanceData = item.data as { id?: string; item?: { id?: string } } | undefined
        const itemId = instanceData?.item?.id || instanceData?.id
        return matchesModel && matchesModule && itemId == id
      }

      return matchesModel && matchesModule
    })
  }

  const generateModuleUrl = (modelName: string, moduleName: string, id: string) => {
    if (!i18n) {
      console.warn('generateModuleUrl requires i18n instance')
      return ''
    }

    if (!crudManagerStore.isInitiated) return ''

    // an editor-shaped URL for a model whose editor lives outside CrudManager
    // goes to the registered screen — no more minting redirect-only URLs
    const externalRoute = externalEditorRouteFor(modelName)
    if (externalRoute && (moduleName === 'editor' || moduleName === 'external') && id) {
      return externalRoute(id)
    }

    const module = getModule(moduleName)
    const modelMeta = getModelMeta(modelName)

    if (!module || !modelMeta) {
      console.warn(`Could not generate URL: module ${moduleName} or model ${modelName} not found`)
      return ''
    }

    const modelSlug = i18n.t(`crudmodels.${modelName}.slug`)

    if (module.name === 'editor') {
      return `/manage/${modelSlug}/${module.slugs[0]}/${id}`
    }

    return `/manage/${modelSlug}/${module.slugs[0]}`
  }

  const goToEditInstance = (modelName: string, id: string) => {
    if (!Router) {
      console.warn('goToEditInstance requires router instance')
      return
    }

    const url = generateModuleUrl(modelName, 'editor', id)
    if (url) {
      Router.push(url)
    }
  }

  const controller = new AbortController()

  const refreshInstance = async (id: string) => {
    const instance = getInstanceById(id)
    if (!instance) return

    if (instance.status.isAwaitingSave === true) {
      //   checkIfSaveRequired();
    } else {
      try {
        const model = await getFullModel(instance.modelName)
        if (!model) {
          console.error(`Could not load model: ${instance.modelName}`)
          return
        }

        instance.status.isLoading = true

        // Use new RESTful API pattern: GET /{model}/{id}
        const response = await axiosApi.get(`cm/${model.apiUrl}/${id}`, {
          signal: controller.signal,
        })

        const instanceData = instance.data as { item?: unknown; originalItem?: unknown }
        instanceData.item = destr(JSON.stringify(response.data))
        instanceData.originalItem = destr(JSON.stringify(response.data))

        const nameResult = model.getName(response.data)
        instance.title = typeof nameResult === 'string' ? nameResult : String(nameResult)
        instance.status.isLoading = false
      } catch (error) {
        console.warn(error)
        instance.status.isLoading = false
      }
    }
  }

  const getInstanceTitle = async (instanceId: string) => {
    const instance = getInstanceById(instanceId)
    if (!instance) return ''

    try {
      const config = await getModelConfig(instance.modelName)
      if (!config) return instance.modelName

      const moduleConfig = config[instance.moduleName] as { getTitle?: (data: unknown) => string } | undefined
      const instanceData = instance.data as { item?: unknown } | undefined
      const data = instanceData?.item || {}

      if (moduleConfig?.getTitle) {
        return moduleConfig.getTitle(data)
      }

      // Fallback to model getName if available
      const model = await getFullModel(instance.modelName)
      return model ? model.getName(data) : instance.modelName
    } catch (error) {
      console.warn(`Could not load model for title: ${instance.modelName}`, error)
      return instance.modelName
    }
  }

  const getInstanceSubtitle = async (instanceId: string) => {
    const instance = getInstanceById(instanceId)
    if (!instance) return ''

    try {
      const config = await getModelConfig(instance.modelName)
      if (!config) return ''

      const moduleConfig = config[instance.moduleName] as { getSubtitle?: (data: unknown) => string } | undefined
      const instanceData = instance.data as { item?: unknown } | undefined
      const data = instanceData?.item || {}
      return moduleConfig?.getSubtitle ? moduleConfig.getSubtitle(data) : ''
    } catch (error) {
      console.warn(`Could not load model for subtitle: ${instance.modelName}`, error)
      return ''
    }
  }

  /**
   * Closes/removes a CRUD instance
   * @param instanceOrId - The instance object or instance ID
   * @param displayLister - Whether to display the lister after closing (default: true)
   * @returns Promise that resolves when the instance is closed
   */
  const closeInstance = async (instanceOrId: CrudInstance | string, displayLister = true): Promise<void> => {
    const instance = typeof instanceOrId === 'string' ? getInstanceById(instanceOrId) : instanceOrId

    if (!instance) {
      console.warn(`Instance not found: ${typeof instanceOrId === 'string' ? instanceOrId : instanceOrId.id}`)
      return
    }

    await crudManagerStore.removeInstance(instance, displayLister)
  }

  return {
    getModelConfig,
    getModelIcon,
    getInstanceTitle,
    getInstanceSubtitle,
    refreshInstance,
    generateModuleUrl,
    instanceExists,
    getModuleConfig,
    getInstanceById,
    getModule,
    getModelMeta,
    getFullModel,
    getModuleBySlug,
    getModelBySlug,
    getModuleComponent,
    getModelComponent,
    openEditor,
    openCreator,
    redirectToLister,
    getChildModels,
    canUserCrud,
    goToEditInstance,
    loadTitleAndSubtitle,
    closeInstance,
  }
}
